import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MainApiService from "../../../../services/mainApi.service";
import {
  AutoCompleteUncontrolled,
  ISelectType,
  Table,
  useFetch,
} from "@4uhub/lib4uhub";
import { IGetServiceDay } from "../../../../models/service-day";
import { IProtocolGenerate } from "../../../../models/protocol-generate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import TicketServService from "../../../../services/ticket.service";

const SERVICE_DAY_ROUTE =
  process.env.REACT_APP_TICKETS + "/api/v1/TicketServiceDays/";
const serviceDayService = new MainApiService<IGetServiceDay>(SERVICE_DAY_ROUTE);

const translationPath = "chatBot.page.service.serviceDay.";
const daysOfWeekTranslation = "components.daysOfWeek.";

const ticketService = new TicketServService();

const ServiceDayPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { eI } = useParams();

  const theme = useTheme();

  const matches = useMediaQuery(theme.breakpoints.down("sm"));

  const { sendRequest } = useFetch(ticketService.listTicketServiceInDays);
  const [ticketServicesOptions, setTicketServiceOptions] = useState<
    ISelectType[]
  >([]);
  const [ticketServiceSelected, setTicketServiceSelected] =
    useState<ISelectType | null>(null);

  const columns: GridColDef<IGetServiceDay>[] = [
    {
      field: "ticketService",
      headerName: t(translationPath + "ticket_service") || "Ticket Service",
      valueGetter: (params) =>
        params.row.ticketService ? params.row.ticketService.name : "",
      renderCell: (params) => params.value || "-",
    },
    {
      field: "dayOfWeek",
      headerName: t(translationPath + "day_of_week") || "Day of week",
      renderCell: (params) =>
        t(daysOfWeekTranslation + params.value.toLowerCase()),
    },
    {
      field: "startTimeTimeOnly",
      headerName: t(translationPath + "start_time") || "Start time",
      renderCell: (params) =>
        params.value ? params.value.substring(0, 5) : "-",
    },
    {
      field: "endTimeTimeOnly",
      headerName: t(translationPath + "end_time") || "End time",
      renderCell: (params) =>
        params.value ? params.value.substring(0, 5) : "-",
    },
  ];

  const handleAdd = () => {
    navigate("new");
  };

  const handleRowClick = (params: GridRowParams<IProtocolGenerate>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const fetchTicketServiceOptions = useCallback(async () => {
    const { data, success } = await sendRequest(undefined);
    if (data && success) {
      setTicketServiceOptions(data);
    }
  }, [sendRequest]);

  const props = useMemo(() => {
    return {
      TicketServiceId: ticketServiceSelected
        ? ticketServiceSelected.id
        : undefined,
    };
  }, [ticketServiceSelected]);

  useEffect(() => {
    fetchTicketServiceOptions();
  }, [fetchTicketServiceOptions]);

  return (
    <Table<IGetServiceDay>
      showDefaultMacroFilters={false}
      service={serviceDayService}
      columns={columns}
      title={t(translationPath + "service_days")}
      searchInput
      onAdd={handleAdd}
      onRowClick={handleRowClick}
      serviceProps={props}
      macroFilters={() => (
        <AutoCompleteUncontrolled
          onValueSelected={setTicketServiceSelected}
          value={ticketServiceSelected}
          label={t(translationPath + "ticket_service")}
          options={ticketServicesOptions}
          sx={{ mt: matches ? 2 : 0, width: 300 }}
          size="small"
          fullWidth
        />
      )}
      searchInputPlaceHolder={
        t(translationPath + "search") || "Search for opening hours"
      }
      addButtonLabel={
        t(translationPath + "add_service_day") || "Add Protocol Generate Rule"
      }
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default ServiceDayPage;
